import { useState, useMemo } from 'react';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';

// ─────────────────────────────────────────────────────────────────────────────
// MARLA LIST: 1–160 + half-marlas 1.5, 2.5 … 9.5
// 20 marla = 1 Kanal, 40 = 2 Kanal, etc.
// ─────────────────────────────────────────────────────────────────────────────
const buildMarlaList = () => {
  const first = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5];
  const rest  = Array.from({ length: 151 }, (_, i) => i + 10);
  return [...first, ...rest].map((m) => {
    const n = Number(m);
    const isKanal = Number.isInteger(n) && n >= 20 && n % 20 === 0;
    const kanal   = isKanal ? n / 20 : null;
    const kStr    = kanal ? ` / ${kanal} Kanal` : '';
    return { key: String(m), label: `${m} Marla${kStr}`, marla: m, sqYards: n * 25 };
  });
};
const MARLA_LIST  = buildMarlaList();
const getMarlaInfo = (k) => MARLA_LIST.find((m) => m.key === k) || MARLA_LIST[2];

// ─────────────────────────────────────────────────────────────────────────────
// INPUT SHEET 1 — 5 rows (A–E)
// ─────────────────────────────────────────────────────────────────────────────
const SHEET1_ROWS = [
  { key: 'A', label: 'Basement Floor Covered Area' },
  { key: 'B', label: 'Ground Floor Covered Area'   },
  { key: 'C', label: 'First Floor Covered Area'    },
  { key: 'D', label: 'Second Floor Covered Area'   },
  { key: 'E', label: 'Mumty Floor Covered Area'    },
];

// ─────────────────────────────────────────────────────────────────────────────
// INPUT SHEET 2 — 9 rate fields (G1–G6, F1–F3), single value each
// ─────────────────────────────────────────────────────────────────────────────
const SHEET2_GREY = [
  { key: 'G1', tag: 'G1', label: 'Budget Basement Grey Structure'                        },
  { key: 'G2', tag: 'G2', label: 'A Quality Basement Grey Structure'                    },
  { key: 'G3', tag: 'G3', label: 'Premium Quality Basement Grey Structure'              },
  { key: 'G4', tag: 'G4', label: 'Budget Ground & Other Floors Grey Structure'          },
  { key: 'G5', tag: 'G5', label: 'A Quality Ground & Other Floors Grey Structure'       },
  { key: 'G6', tag: 'G6', label: 'Premium Quality Ground & Other Floors Grey Structure' },
];
const SHEET2_FINISH = [
  { key: 'F1', tag: 'F1', label: 'Budget Finishes'          },
  { key: 'F2', tag: 'F2', label: 'A Quality Finishes'       },
  { key: 'F3', tag: 'F3', label: 'Premium Quality Finishes' },
];
const SHEET2_ALL = [...SHEET2_GREY, ...SHEET2_FINISH];

// ─────────────────────────────────────────────────────────────────────────────
// 6 HOUSE TYPES — reference table
// ─────────────────────────────────────────────────────────────────────────────
const HOUSE_TYPES = [
  { id:1, label:'Single Storey House with Mumty',                                       areaKeys:['B','E'],             formula:'B + E'         },
  { id:2, label:'Double Storey House — Ground + First + Mumty',                         areaKeys:['B','C','E'],         formula:'B + C + E'     },
  { id:3, label:'Double Storey House — Basement + Ground + Mumty',                      areaKeys:['A','B','E'],         formula:'A + B + E'     },
  { id:4, label:'Triple Storey House — Ground + First + Second + Mumty',                areaKeys:['B','C','D','E'],     formula:'B + C + D + E' },
  { id:5, label:'Triple Storey House — Basement + Ground + First + Mumty',              areaKeys:['A','B','C','E'],     formula:'A + B + C + E' },
  { id:6, label:'Four Storey House — Basement + Ground + First + Second + Mumty',       areaKeys:['A','B','C','D','E'], formula:'A + B + C + D + E' },
];

const HT_INTRO = {
  1: (ds) => `Now let's see cost of single storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has ground floor and mumty floor:`,
  2: (ds) => `Now let's see cost of double storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has ground floor, first floor and a mumty floor:`,
  3: (ds) => `Now let's see cost of double storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has basement floor, ground floor and a mumty floor:`,
  4: (ds) => `Now let's see cost of triple storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has ground floor, first floor, second floor and a mumty floor:`,
  5: (ds) => `Now let's see cost of triple storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has basement floor, ground floor, first floor and a mumty floor:`,
  6: (ds) => `Now let's see cost of four storey house of ${ds} in Budget, A Quality and Premium Quality finishes which has basement floor, ground floor, first floor, second floor and a mumty floor:`,
};

// Sq Yards list (same marla keys, different label for the dropdown)
const SQ_YARDS_LIST = MARLA_LIST.map((m) => ({ key: m.key, label: `${m.sqYards} Sq Yards` }));

// Compute display string based on preview mode
const getDisplayStr = (marlaInfo, mode) =>
  mode === 'marla' ? `${marlaInfo.marla} Marla` : `${marlaInfo.sqYards} Square Yards`;

// ─────────────────────────────────────────────────────────────────────────────
// QUALITY CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const QUALITY_CFG = {
  budget:   { label:'Budget',    badgeBg:'#dbeafe', badgeColor:'#1e40af', headBg:'#eff6ff' },
  aQuality: { label:'A Quality', badgeBg:'#dcfce7', badgeColor:'#14532d', headBg:'#f0fdf4' },
  premium:  { label:'Premium',   badgeBg:'#fef3c7', badgeColor:'#92400e', headBg:'#fffbeb' },
};

// ─────────────────────────────────────────────────────────────────────────────
// 18 TABLE CONFIGS — exact formulas from client document
// Grey cost   = area(aK) × rate(gK)
// Finish cost = area(aK) × rate(fK)
// ─────────────────────────────────────────────────────────────────────────────
const TABLE_CONFIGS = [
  // ── 1. Single Storey (Ground B + Mumty E)
  { id:1, htId:1, quality:'budget',
    title:'BUDGET SINGLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of single storey [ML] house in budget finishes:',
    rows:[{ label:'Ground Floor', aK:'B', gK:'G4', fK:'F1' }, { label:'Mumty Floor', aK:'E', gK:'G4', fK:'F1' }],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} single storey house with mumty in budget quality is PKR ${tot}`,
  },
  { id:2, htId:1, quality:'aQuality',
    title:'A QUALITY SINGLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of single storey [ML] house in A quality finishes:',
    rows:[{ label:'Ground Floor', aK:'B', gK:'G5', fK:'F2' }, { label:'Mumty Floor', aK:'E', gK:'G5', fK:'F2' }],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} single storey house with mumty in A quality is PKR ${tot}`,
  },
  { id:3, htId:1, quality:'premium',
    title:'PREMIUM QUALITY SINGLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of single storey [ML] house in premium finishes:',
    rows:[{ label:'Ground Floor', aK:'B', gK:'G6', fK:'F3' }, { label:'Mumty Floor', aK:'E', gK:'G6', fK:'F3' }],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} single storey house with mumty in premium quality is PKR ${tot}`,
  },

  // ── 2. Double Storey — Ground + First + Mumty (B+C+E)
  { id:4, htId:2, quality:'budget',
    title:'BUDGET DOUBLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of double storey [ML] house in budget finishes:',
    rows:[
      { label:'Ground Floor', aK:'B', gK:'G4', fK:'F1' },
      { label:'First Floor',  aK:'C', gK:'G4', fK:'F1' },
      { label:'Mumty Floor',  aK:'E', gK:'G4', fK:'F1' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house with ground floor, first floor and mumty in budget quality is PKR ${tot}`,
  },
  { id:5, htId:2, quality:'aQuality',
    title:'A QUALITY DOUBLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of double storey [ML] house in A quality finishes:',
    rows:[
      { label:'Ground Floor', aK:'B', gK:'G5', fK:'F2' },
      { label:'First Floor',  aK:'C', gK:'G5', fK:'F2' },
      { label:'Mumty Floor',  aK:'E', gK:'G5', fK:'F2' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house with ground floor, first floor and mumty in A quality is PKR ${tot}`,
  },
  { id:6, htId:2, quality:'premium',
    title:'PREMIUM QUALITY DOUBLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of double storey [ML] house in premium finishes:',
    rows:[
      { label:'Ground Floor', aK:'B', gK:'G6', fK:'F3' },
      { label:'First Floor',  aK:'C', gK:'G6', fK:'F3' },
      { label:'Mumty Floor',  aK:'E', gK:'G6', fK:'F3' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house with mumty in premium quality is PKR ${tot}`,
  },

  // ── 3. Double Storey — Basement + Ground + Mumty (A+B+E)
  { id:7, htId:3, quality:'budget',
    title:'BUDGET DOUBLE STOREY [ML] HOUSE COST (Basement + Ground)',
    subTitle:'Cost of double storey [ML] house in budget finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G1', fK:'F1' },
      { label:'Ground Floor',   aK:'B', gK:'G4', fK:'F1' },
      { label:'Mumty Floor',    aK:'E', gK:'G4', fK:'F1' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house with basement, ground floor and mumty in budget quality is PKR ${tot}`,
  },
  { id:8, htId:3, quality:'aQuality',
    title:'A QUALITY DOUBLE STOREY [ML] HOUSE COST (Basement + Ground)',
    subTitle:'Cost of double storey [ML] house in A quality finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G2', fK:'F2' },
      { label:'Ground Floor',   aK:'B', gK:'G5', fK:'F2' },
      { label:'Mumty Floor',    aK:'E', gK:'G5', fK:'F2' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house where basement, ground floor and mumty is constructed in A quality is PKR ${tot}`,
  },
  { id:9, htId:3, quality:'premium',
    title:'PREMIUM QUALITY DOUBLE STOREY [ML] HOUSE COST (Basement + Ground)',
    subTitle:'Cost of double storey [ML] house in premium finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G3', fK:'F3' },
      { label:'Ground Floor',   aK:'B', gK:'G6', fK:'F3' },
      { label:'Mumty Floor',    aK:'E', gK:'G6', fK:'F3' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} double storey house with mumty where basement and ground floor is constructed in premium quality is PKR ${tot}`,
  },

  // ── 4. Triple Storey — Ground + First + Second + Mumty (B+C+D+E)
  { id:10, htId:4, quality:'budget',
    title:'BUDGET TRIPLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of triple storey [ML] house in budget finishes:',
    rows:[
      { label:'Ground Floor',  aK:'B', gK:'G4', fK:'F1' },
      { label:'First Floor',   aK:'C', gK:'G4', fK:'F1' },
      { label:'Second Floor',  aK:'D', gK:'G4', fK:'F1' },
      { label:'Mumty Floor',   aK:'E', gK:'G4', fK:'F1' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house with ground, first, second floor and mumty in budget quality is PKR ${tot}`,
  },
  { id:11, htId:4, quality:'aQuality',
    title:'A QUALITY TRIPLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of triple storey [ML] house in A quality finishes:',
    rows:[
      { label:'Ground Floor',  aK:'B', gK:'G5', fK:'F2' },
      { label:'First Floor',   aK:'C', gK:'G5', fK:'F2' },
      { label:'Second Floor',  aK:'D', gK:'G5', fK:'F2' },
      { label:'Mumty Floor',   aK:'E', gK:'G5', fK:'F2' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house where ground, first, second floor and mumty is constructed in A quality is PKR ${tot}`,
  },
  { id:12, htId:4, quality:'premium',
    title:'PREMIUM QUALITY TRIPLE STOREY [ML] HOUSE COST',
    subTitle:'Cost of triple storey [ML] house in premium finishes:',
    rows:[
      { label:'Ground Floor',  aK:'B', gK:'G6', fK:'F3' },
      { label:'First Floor',   aK:'C', gK:'G6', fK:'F3' },
      { label:'Second Floor',  aK:'D', gK:'G6', fK:'F3' },
      { label:'Mumty Floor',   aK:'E', gK:'G6', fK:'F3' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house where ground floor, first floor, second floor and mumty is constructed in premium quality is PKR ${tot}`,
  },

  // ── 5. Triple Storey — Basement + Ground + First + Mumty (A+B+C+E)
  { id:13, htId:5, quality:'budget',
    title:'BUDGET TRIPLE STOREY [ML] HOUSE COST (Basement)',
    subTitle:'Cost of triple storey [ML] house in budget finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G1', fK:'F1' },
      { label:'Ground Floor',   aK:'B', gK:'G4', fK:'F1' },
      { label:'First Floor',    aK:'C', gK:'G4', fK:'F1' },
      { label:'Mumty Floor',    aK:'E', gK:'G4', fK:'F1' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house with basement, ground, first floor and mumty in budget quality is PKR ${tot}`,
  },
  { id:14, htId:5, quality:'aQuality',
    title:'A QUALITY TRIPLE STOREY [ML] HOUSE COST (Basement)',
    subTitle:'Cost of triple storey [ML] house in A quality finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G2', fK:'F2' },
      { label:'Ground Floor',   aK:'B', gK:'G5', fK:'F2' },
      { label:'First Floor',    aK:'C', gK:'G5', fK:'F2' },
      { label:'Mumty Floor',    aK:'E', gK:'G5', fK:'F2' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house where basement, ground, first floor and mumty is constructed in A quality is PKR ${tot}`,
  },
  { id:15, htId:5, quality:'premium',
    title:'PREMIUM QUALITY TRIPLE STOREY [ML] HOUSE COST (Basement)',
    subTitle:'Cost of triple storey [ML] house in premium finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G3', fK:'F3' },
      { label:'Ground Floor',   aK:'B', gK:'G6', fK:'F3' },
      { label:'First Floor',    aK:'C', gK:'G6', fK:'F3' },
      { label:'Mumty Floor',    aK:'E', gK:'G6', fK:'F3' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} triple storey house where basement, ground, first floor and mumty is constructed in premium quality is PKR ${tot}`,
  },

  // ── 6. Four Storey — Basement + Ground + First + Second + Mumty (A+B+C+D+E)
  { id:16, htId:6, quality:'budget',
    title:'BUDGET FOUR STOREY [ML] HOUSE COST',
    subTitle:'Cost of four storey [ML] house in budget finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G1', fK:'F1' },
      { label:'Ground Floor',   aK:'B', gK:'G4', fK:'F1' },
      { label:'First Floor',    aK:'C', gK:'G4', fK:'F1' },
      { label:'Second Floor',   aK:'D', gK:'G4', fK:'F1' },
      { label:'Mumty Floor',    aK:'E', gK:'G4', fK:'F1' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} four storey house with basement, ground, first, second floor and mumty in budget quality is PKR ${tot}`,
  },
  { id:17, htId:6, quality:'aQuality',
    title:'A QUALITY FOUR STOREY [ML] HOUSE COST',
    subTitle:'Cost of four storey [ML] house in A quality finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G2', fK:'F2' },
      { label:'Ground Floor',   aK:'B', gK:'G5', fK:'F2' },
      { label:'First Floor',    aK:'C', gK:'G5', fK:'F2' },
      { label:'Second Floor',   aK:'D', gK:'G5', fK:'F2' },
      { label:'Mumty Floor',    aK:'E', gK:'G5', fK:'F2' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} four storey house where basement, ground, first, second floor and mumty is constructed in A quality is PKR ${tot}`,
  },
  { id:18, htId:6, quality:'premium',
    title:'PREMIUM QUALITY FOUR STOREY [ML] HOUSE COST',
    subTitle:'Cost of four storey [ML] house in premium finishes:',
    rows:[
      { label:'Basement Floor', aK:'A', gK:'G3', fK:'F3' },
      { label:'Ground Floor',   aK:'B', gK:'G6', fK:'F3' },
      { label:'First Floor',    aK:'C', gK:'G6', fK:'F3' },
      { label:'Second Floor',   aK:'D', gK:'G6', fK:'F3' },
      { label:'Mumty Floor',    aK:'E', gK:'G6', fK:'F3' },
    ],
    summaryFn:(ds,tot)=>`The cost of constructing ${ds} four storey house where basement, ground, first, second floor and mumty is constructed in premium quality is PKR ${tot}`,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const safeN  = (v) => (v === '' || v == null ? 0 : Number(v));
const fmt    = (v) => (v ? Number(v).toLocaleString('en-PK', { maximumFractionDigits: 0 }) : '—');
const fmtRs  = (v) => (v ? 'Rs ' + Number(v).toLocaleString('en-PK', { maximumFractionDigits: 0 }) : '—');
const kd     = (e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); };
const repML  = (str, ml) => str.replace(/\[ML\]/g, ml);

const buildSheet1 = () => {
  const g = {};
  SHEET1_ROWS.forEach(({ key }) => {
    g[key] = {};
    MARLA_LIST.forEach(({ key: c }) => { g[key][c] = ''; });
  });
  return g;
};
const buildSheet2 = () => {
  const o = {};
  SHEET2_ALL.forEach(({ key }) => { o[key] = ''; });
  return o;
};

const computeTable = (config, marlaKey, sheet1, sheet2) => {
  const rows = config.rows.map((r) => {
    const area = safeN(sheet1[r.aK]?.[marlaKey]);
    const grey = area * safeN(sheet2[r.gK]);
    const fin  = area * safeN(sheet2[r.fK]);
    return { label: r.label, area, grey, fin };
  });
  return {
    rows,
    totArea: rows.reduce((s, r) => s + r.area, 0),
    totGrey: rows.reduce((s, r) => s + r.grey, 0),
    totFin:  rows.reduce((s, r) => s + r.fin, 0),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 1 GRID COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const CELL_W  = 90;
const LABEL_W = 260;

const Sheet1Grid = ({ data, onChange }) => (
  <div className='ui-card overflow-hidden p-0'>
    <div className='flex items-center gap-3 border-b border-[#15173d]/[0.06] px-5 py-4'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#982598]/10'>
        <span className='text-[11px] font-bold text-[#982598]'>1</span>
      </div>
      <div className='flex-1'>
        <h3 className='text-[11px] font-semibold uppercase tracking-[0.18em] text-[#982598]'>Input Sheet 1 — Floor Covered Areas</h3>
        <p className='text-[11px] text-gray-400'>Enter sq ft per floor for each marla size (1–160 + half marlas 1.5–9.5). Scroll right.</p>
      </div>
      <span className='hidden sm:block rounded-full bg-[#f5eeff] px-2.5 py-1 text-[10px] font-semibold text-[#982598] shrink-0'>
        5 rows × {MARLA_LIST.length} cols
      </span>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: (LABEL_W + MARLA_LIST.length * CELL_W) + 'px' }}>
        <thead>
          <tr>
            <th style={{ position:'sticky',left:0,zIndex:20,background:'linear-gradient(135deg,#15173d,#2d1050)',minWidth:LABEL_W,width:LABEL_W,padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.7)',borderRight:'2px solid rgba(255,255,255,0.15)' }}>
              Floor (A–E)
            </th>
            {MARLA_LIST.map(({ key, label }) => {
              const n = Number(key);
              const major = Number.isInteger(n) && n >= 10 && n % 10 === 0;
              return (
                <th key={key} style={{ background:'linear-gradient(135deg,#15173d,#2d1050)',minWidth:CELL_W,width:CELL_W,padding:'9px 3px',textAlign:'center',fontSize:9,fontWeight:600,whiteSpace:'nowrap',color:major?'#f0c6ff':'rgba(255,255,255,0.5)',borderRight:major?'1px solid rgba(152,37,152,0.5)':'1px solid rgba(255,255,255,0.05)' }}>
                  {label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {SHEET1_ROWS.map(({ key: rk, label }, ri) => {
            const bg = ri % 2 === 0 ? '#fff' : '#faf9ff';
            return (
              <tr key={rk}>
                <td style={{ position:'sticky',left:0,zIndex:10,background:bg,minWidth:LABEL_W,width:LABEL_W,padding:'7px 12px',fontSize:11,fontWeight:700,color:'#15173d',borderRight:'2px solid #f0e4f0',borderBottom:'1px solid #f3f0f8',whiteSpace:'nowrap' }}>
                  <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:22,height:22,borderRadius:6,background:'linear-gradient(135deg,#982598,#2d1050)',color:'#fff',fontSize:11,fontWeight:900,marginRight:8,flexShrink:0,verticalAlign:'middle' }}>{rk}</span>
                  {label}
                </td>
                {MARLA_LIST.map(({ key: col }) => {
                  const n = Number(col);
                  const major = Number.isInteger(n) && n >= 10 && n % 10 === 0;
                  return (
                    <td key={col} style={{ background:bg,padding:'3px',borderRight:major?'1px solid #f0e4f0':'1px solid #f5f3fa',borderBottom:'1px solid #f3f0f8' }}>
                      <input
                        type='number' min='0' step='any' dir='ltr' placeholder='0'
                        value={data[rk]?.[col] ?? ''}
                        onChange={(e) => { if (e.target.value !== '' && Number(e.target.value) < 0) return; onChange(rk, col, e.target.value); }}
                        onKeyDown={kd}
                        onFocus={(e) => { e.target.style.background = '#f5f0ff'; e.target.style.outline = '1.5px solid #982598'; e.target.style.borderRadius = '4px'; }}
                        onBlur={(e)  => { e.target.style.background = 'transparent'; e.target.style.outline = 'none'; }}
                        style={{ width:'100%',border:'none',outline:'none',background:'transparent',fontSize:11,textAlign:'center',color:'#15173d',fontWeight:500,padding:'4px 2px',borderRadius:4 }}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 2 FORM (9 fields, no marla columns)
// ─────────────────────────────────────────────────────────────────────────────
const Sheet2Form = ({ data, onChange }) => (
  <div className='ui-card'>
    <div className='mb-5 flex items-center gap-3'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#982598]/10'>
        <span className='text-[11px] font-bold text-[#982598]'>2</span>
      </div>
      <div>
        <h3 className='text-[11px] font-semibold uppercase tracking-[0.18em] text-[#982598]'>Input Sheet 2 — Construction Rates / Sq Ft (PKR)</h3>
        <p className='text-[11px] text-gray-400'>One value each — same rate applies to all marla sizes</p>
      </div>
    </div>

    {/* Grey Structure G1–G6 */}
    <div className='mb-6'>
      <div className='mb-3 flex items-center gap-2'>
        <div style={{ width:10,height:10,borderRadius:3,background:'linear-gradient(135deg,#15173d,#2d1050)' }} />
        <p className='text-[11px] font-bold uppercase tracking-wider text-[#15173d]'>Grey Structure Rates — G1 to G6</p>
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {SHEET2_GREY.map(({ key, tag, label }) => (
          <div key={key}>
            <label className='ui-form-label flex items-center gap-1.5'>
              <span style={{ display:'inline-block',padding:'1px 7px',borderRadius:4,background:'#15173d',color:'#f0c6ff',fontSize:10,fontWeight:800 }}>{tag}</span>
              {label}
            </label>
            <div className='relative'>
              <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:10,fontWeight:700,color:'#982598',pointerEvents:'none' }}>PKR</span>
              <input type='number' min='0' step='any' dir='ltr' placeholder='0'
                value={data[key] ?? ''}
                onChange={(e) => { if (e.target.value !== '' && Number(e.target.value) < 0) return; onChange(key, e.target.value); }}
                onKeyDown={kd} className='ui-input-field' style={{ paddingLeft:44 }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Finish F1–F3 */}
    <div>
      <div className='mb-3 flex items-center gap-2'>
        <div style={{ width:10,height:10,borderRadius:3,background:'linear-gradient(135deg,#982598,#c026d3)' }} />
        <p className='text-[11px] font-bold uppercase tracking-wider text-[#982598]'>Finishing Rates — F1 to F3</p>
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
        {SHEET2_FINISH.map(({ key, tag, label }) => (
          <div key={key}>
            <label className='ui-form-label flex items-center gap-1.5'>
              <span style={{ display:'inline-block',padding:'1px 7px',borderRadius:4,background:'#982598',color:'#fff',fontSize:10,fontWeight:800 }}>{tag}</span>
              {label}
            </label>
            <div className='relative'>
              <span style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:10,fontWeight:700,color:'#982598',pointerEvents:'none' }}>PKR</span>
              <input type='number' min='0' step='any' dir='ltr' placeholder='0'
                value={data[key] ?? ''}
                onChange={(e) => { if (e.target.value !== '' && Number(e.target.value) < 0) return; onChange(key, e.target.value); }}
                onKeyDown={kd} className='ui-input-field' style={{ paddingLeft:44 }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// REFERENCE TABLE — 6 house types with total sq ft
// ─────────────────────────────────────────────────────────────────────────────
const ReferenceTable = ({ marlaInfo, sheet1 }) => {
  const areas = {};
  SHEET1_ROWS.forEach(({ key }) => { areas[key] = safeN(sheet1[key]?.[marlaInfo.key]); });
  return (
    <div className='ui-card overflow-hidden p-0'>
      <div className='border-b border-[#15173d]/[0.06] bg-gradient-to-r from-[#15173d] to-[#2d1050] px-5 py-3.5'>
        <p className='text-[10px] font-semibold uppercase tracking-widest text-white/50 mb-0.5'>Area Reference Table</p>
        <p className='text-[13px] font-extrabold text-white'>
          Square Feet Area of Different Houses — {marlaInfo.label} ({marlaInfo.sqYards} Sq Yards)
        </p>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse',width:'100%',minWidth:550 }}>
          <thead>
            <tr>
              <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',minWidth:260 }}>Type of House</th>
              <th style={{ background:'#f8f5ff',padding:'9px 14px',textAlign:'center',fontSize:11,fontWeight:700,color:'#9ca3af',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',fontFamily:'monospace' }}>Formula</th>
              <th style={{ background:'#f8f5ff',padding:'9px 16px',textAlign:'right',fontSize:11,fontWeight:700,color:'#982598',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Total Sq Ft Constructed</th>
            </tr>
          </thead>
          <tbody>
            {HOUSE_TYPES.map((ht, ri) => {
              const total = ht.areaKeys.reduce((s, k) => s + (areas[k] || 0), 0);
              return (
                <tr key={ht.id} style={{ background: ri%2===0?'#fff':'#faf9ff' }}>
                  <td style={{ padding:'9px 14px',fontSize:12,fontWeight:600,color:'#15173d',borderBottom:'1px solid #f5f3fa' }}>{ht.label}</td>
                  <td style={{ padding:'9px 14px',fontSize:11,color:'#9ca3af',fontFamily:'monospace',textAlign:'center',borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{ht.formula}</td>
                  <td style={{ padding:'9px 16px',fontSize:14,fontWeight:800,textAlign:'right',color:total?'#15173d':'#d1d5db',borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>
                    {total ? fmt(total) + ' sq ft' : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE COST TABLE (collapsible)
// ─────────────────────────────────────────────────────────────────────────────
const CostTable = ({ config, marlaInfo, sheet1, sheet2, defaultOpen, displayStr }) => {
  const [open, setOpen] = useState(defaultOpen);
  const qc  = QUALITY_CFG[config.quality];
  const ml  = displayStr;

  const { rows, totArea, totGrey, totFin } = useMemo(
    () => computeTable(config, marlaInfo.key, sheet1, sheet2),
    [config, marlaInfo.key, sheet1, sheet2]
  );
  const total   = totGrey + totFin;
  const hasData = rows.some((r) => r.area > 0);

  return (
    <div style={{ overflow:'hidden',borderRadius:12,border:'1px solid rgba(21,23,61,0.07)',background:'#fff',boxShadow:'0 2px 8px rgba(21,23,61,0.04)' }}>
      <button type='button' onClick={() => setOpen((o) => !o)}
        className='flex w-full items-center justify-between px-5 py-3 text-left hover:bg-gray-50/70 transition-colors'>
        <div className='flex items-center gap-2 flex-1 min-w-0 flex-wrap'>
          <span style={{ fontSize:10,fontWeight:700,background:qc.badgeBg,color:qc.badgeColor,borderRadius:5,padding:'2px 9px',whiteSpace:'nowrap',flexShrink:0 }}>{qc.label}</span>
          <span style={{ fontSize:11,fontWeight:700,color:'#15173d',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{repML(config.title, ml)}</span>
          {!hasData && <span style={{ borderRadius:999,background:'#fffbeb',border:'1px solid #fcd34d',padding:'1px 8px',fontSize:9,fontWeight:600,color:'#92400e',flexShrink:0 }}>No data</span>}
        </div>
        <div className='flex items-center gap-2 ml-2 shrink-0'>
          {hasData && total > 0 && <span style={{ fontSize:11,fontWeight:700,color:'#6b7280' }}>{fmtRs(total)}</span>}
          {open ? <ChevronUp size={14} className='text-gray-400'/> : <ChevronDown size={14} className='text-gray-400'/>}
        </div>
      </button>

      {open && (
        <div style={{ borderTop:'1px solid #f3f0f8' }}>
          <div style={{ padding:'8px 20px 4px',background:qc.headBg }}>
            <p style={{ fontSize:11,color:'#6b7280',fontStyle:'italic' }}>{repML(config.subTitle, ml)}</p>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ borderCollapse:'collapse',width:'100%',minWidth:440 }}>
              <thead>
                <tr>
                  <th style={{ background:'#f8f5ff',padding:'8px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0' }}>Floor</th>
                  <th style={{ background:'#f8f5ff',padding:'8px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Area (Sq Ft)</th>
                  <th style={{ background:'#f8f5ff',padding:'8px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:qc.badgeColor,borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Grey Structure</th>
                  <th style={{ background:'#f8f5ff',padding:'8px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:qc.badgeColor,borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Finishing</th>
                  <th style={{ background:'#f8f5ff',padding:'8px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:'#15173d',borderBottom:'2px solid #f0e4f0',borderLeft:'1px solid #f0e4f0',whiteSpace:'nowrap' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={row.label} style={{ background:ri%2===0?'#fff':'#faf9ff' }}>
                    <td style={{ padding:'9px 14px',fontSize:12,fontWeight:600,color:'#374151',borderBottom:'1px solid #f5f3fa' }}>{row.label}</td>
                    <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#6b7280',borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmt(row.area)}</td>
                    <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#374151',fontWeight:500,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.grey)}</td>
                    <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#374151',fontWeight:500,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.fin)}</td>
                    <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#15173d',fontWeight:700,borderBottom:'1px solid #f5f3fa',borderLeft:'1px solid #f5f3fa' }}>{fmtRs(row.grey + row.fin)}</td>
                  </tr>
                ))}
                <tr style={{ background:'linear-gradient(135deg,#15173d,#2d1050)' }}>
                  <td style={{ padding:'9px 14px',fontSize:12,fontWeight:800,color:'#fff' }}>TOTAL</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'rgba(255,255,255,0.6)',fontWeight:600,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmt(totArea)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#c4b5fd',fontWeight:800,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(totGrey)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#c4b5fd',fontWeight:800,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(totFin)}</td>
                  <td style={{ padding:'9px 14px',fontSize:12,textAlign:'right',color:'#f0c6ff',fontWeight:900,borderLeft:'1px solid rgba(255,255,255,0.1)' }}>{fmtRs(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {hasData && total > 0 && (
            <div style={{ padding:'10px 20px',background:'#faf9ff',borderTop:'1px solid #f3f0f8' }}>
              <p style={{ fontSize:12,color:'#374151',lineHeight:1.7,fontStyle:'italic' }}>
                {config.summaryFn(displayStr, fmtRs(total))}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HOUSE TYPE GROUP — intro text + 3 quality tables
// ─────────────────────────────────────────────────────────────────────────────
const HouseTypeGroup = ({ htId, marlaInfo, sheet1, sheet2, displayStr }) => {
  const ht     = HOUSE_TYPES.find((h) => h.id === htId);
  const tables = TABLE_CONFIGS.filter((t) => t.htId === htId);

  return (
    <div className='space-y-2.5'>
      <div className='flex items-center gap-3 pt-1 px-1'>
        <div style={{ width:32,height:32,borderRadius:10,background:'linear-gradient(135deg,#982598,#2d1050)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:'0 4px 14px rgba(152,37,152,0.3)' }}>
          <span style={{ fontSize:14,fontWeight:900,color:'#fff' }}>{htId}</span>
        </div>
        <div>
          <p style={{ fontSize:13,fontWeight:800,color:'#15173d',lineHeight:1.3 }}>{ht.label}</p>
          <p style={{ fontSize:10,fontFamily:'monospace',color:'#9ca3af' }}>Total Sq Ft = {ht.formula}</p>
        </div>
      </div>
      <div style={{ borderRadius:12,border:'1px solid rgba(152,37,152,0.12)',background:'#f5eeff',padding:'10px 16px' }}>
        <p style={{ fontSize:12,color:'#4b5563',lineHeight:1.7 }}>{HT_INTRO[htId](displayStr)}</p>
      </div>
      {tables.map((config) => (
        <CostTable key={config.id} config={config} marlaInfo={marlaInfo}
          sheet1={sheet1} sheet2={sheet2} defaultOpen={config.quality === 'budget'}
          displayStr={displayStr} />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const CostOfConstruction = () => {
  const [sheet1, setSheet1]             = useState(buildSheet1);
  const [sheet2, setSheet2]             = useState(buildSheet2);
  const [previewKey, setPreviewKey]     = useState('3');
  const [previewMode, setPreviewMode]   = useState('marla'); // 'marla' | 'sqYards'
  const [isSubmitting, setIsSubmitting] = useState(false);

  const marlaInfo   = useMemo(() => getMarlaInfo(previewKey), [previewKey]);
  const displayStr  = useMemo(() => getDisplayStr(marlaInfo, previewMode), [marlaInfo, previewMode]);

  // When sq yards dropdown changes, find the matching marla key
  const handleSqYardsChange = (sqYards) => {
    const match = MARLA_LIST.find((m) => String(m.sqYards) === sqYards);
    if (match) { setPreviewKey(match.key); setPreviewMode('sqYards'); }
  };

  const handleSheet1Change = (rk, col, val) =>
    setSheet1((prev) => ({ ...prev, [rk]: { ...prev[rk], [col]: val } }));
  const handleSheet2Change = (key, val) =>
    setSheet2((prev) => ({ ...prev, [key]: val }));
  const handleReset = () => { setSheet1(buildSheet1()); setSheet2(buildSheet2()); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // TODO: API — payload: { sheet1, sheet2 }
      await new Promise((r) => setTimeout(r, 800));
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Data saved successfully.', confirmButtonText: 'OK' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to save.' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-5 pb-8'>

      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h2 className='text-xl font-extrabold text-[#15173d]'>Cost of Construction</h2>
          <p className='text-xs text-gray-500'>
            Update input sheets → 18 auto-calculated blog cost tables (6 house types × 3 quality levels)
          </p>
        </div>
        <div className='flex gap-2'>
          <button type='button' className='ui-btn-cancel' onClick={handleReset} disabled={isSubmitting}>Reset</button>
          <button type='submit' className='ui-btn-save' disabled={isSubmitting}>
            <Save size={15} />
            {isSubmitting ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div className='flex items-start gap-2 rounded-xl border border-[#982598]/15 bg-[#f5eeff] px-4 py-3'>
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#982598' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' style={{ marginTop:1,flexShrink:0 }}>
          <circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/>
        </svg>
        <p className='text-[11px] font-medium text-[#982598]'>
          <strong>Sheet 1:</strong> 5 rows A–E (Basement, Ground, First, Second, Mumty), one column per marla (1–160 + half marlas). &nbsp;
          <strong>Sheet 2:</strong> G1–G6 grey structure rates + F1–F3 finish rates — single value each. &nbsp;
          <strong>Below:</strong> Select marla to preview 18 generated cost tables exactly as they appear in blogs.
        </p>
      </div>

      {/* Sheet 1 */}
      <Sheet1Grid data={sheet1} onChange={handleSheet1Change} />

      {/* Sheet 2 */}
      <Sheet2Form data={sheet2} onChange={handleSheet2Change} />

      {/* Divider + TWO Preview Dropdowns */}
      <div className='flex flex-wrap items-center gap-3 pt-2'>
        <div className='h-px flex-1 bg-[#f0e4f0] min-w-[20px]' />
        <p style={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',color:'#9ca3af',whiteSpace:'nowrap' }}>Preview Blog for:</p>

        {/* Marla Dropdown */}
        <div style={{ display:'flex',alignItems:'center',gap:8,borderRadius:999,border: previewMode==='marla' ? '1.5px solid #982598' : '1.5px solid rgba(152,37,152,0.2)',background: previewMode==='marla' ? '#f5eeff' : '#fafafa',padding:'7px 14px',cursor:'pointer',transition:'all 0.2s' }}
          onClick={() => setPreviewMode('marla')}>
          <span style={{ fontSize:10,fontWeight:700,color: previewMode==='marla' ? '#982598' : '#9ca3af',whiteSpace:'nowrap' }}>🏠 Marla Blog</span>
          <select value={previewKey}
            onChange={(e) => { setPreviewKey(e.target.value); setPreviewMode('marla'); }}
            style={{ border:'none',background:'transparent',fontSize:12,fontWeight:700,color: previewMode==='marla'?'#15173d':'#6b7280',outline:'none',cursor:'pointer',maxWidth:160 }}>
            {MARLA_LIST.map(({ key, label }) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <span style={{ fontSize:11,color:'#d1d5db' }}>|</span>

        {/* Sq Yards Dropdown */}
        <div style={{ display:'flex',alignItems:'center',gap:8,borderRadius:999,border: previewMode==='sqYards' ? '1.5px solid #982598' : '1.5px solid rgba(152,37,152,0.2)',background: previewMode==='sqYards' ? '#f5eeff' : '#fafafa',padding:'7px 14px',cursor:'pointer',transition:'all 0.2s' }}
          onClick={() => setPreviewMode('sqYards')}>
          <span style={{ fontSize:10,fontWeight:700,color: previewMode==='sqYards' ? '#982598' : '#9ca3af',whiteSpace:'nowrap' }}>📐 Sq Yards Blog</span>
          <select
            value={String(marlaInfo.sqYards)}
            onChange={(e) => handleSqYardsChange(e.target.value)}
            style={{ border:'none',background:'transparent',fontSize:12,fontWeight:700,color: previewMode==='sqYards'?'#15173d':'#6b7280',outline:'none',cursor:'pointer',maxWidth:160 }}>
            {SQ_YARDS_LIST.map(({ key, label }) => (
              <option key={key} value={String(MARLA_LIST.find(m=>m.key===key)?.sqYards)}>{label}</option>
            ))}
          </select>
        </div>

        <div className='h-px flex-1 bg-[#f0e4f0] min-w-[20px]' />
      </div>

      {/* Selected size banner — mode-aware */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12,borderRadius:18,background:'linear-gradient(135deg,#15173d 0%,#2d1050 100%)',padding:'18px 24px',boxShadow:'0 8px 28px rgba(21,23,61,0.18)' }}>
        <div>
          <p style={{ fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.15em',color:'rgba(255,255,255,0.45)',marginBottom:2 }}>
            {previewMode === 'marla' ? '🏠 Marla Blog Preview' : '📐 Square Yards Blog Preview'}
          </p>
          <p style={{ fontSize:28,fontWeight:900,color:'#fff',lineHeight:1 }}>{displayStr}</p>
          <p style={{ fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:4 }}>
            {previewMode === 'marla'
              ? `≡ ${marlaInfo.sqYards} Sq Yards (shown only in Marla blog mode)`
              : `≡ ${marlaInfo.marla} Marla (shown only in Sq Yards blog mode)`}
          </p>
        </div>
        <div style={{ textAlign:'right' }}>
          <p style={{ fontSize:10,color:'rgba(255,255,255,0.35)',textTransform:'uppercase',letterSpacing:'0.1em' }}>Generated Tables</p>
          <p style={{ fontSize:36,fontWeight:900,color:'#f0c6ff',lineHeight:1 }}>18</p>
          <p style={{ fontSize:10,color:'rgba(255,255,255,0.3)' }}>6 house types × 3 quality levels</p>
        </div>
      </div>

      {/* Blog intro text — shows only selected unit */}
      <div style={{ borderRadius:12,border:'1px solid #e5e7eb',background:'#fff',padding:'14px 20px' }}>
        <p style={{ fontSize:12,color:'#4b5563',lineHeight:1.9 }}>
          Let's see what will be the area of each type of house if you construct a house on{' '}
          <strong>{displayStr}</strong>:
        </p>
      </div>

      {/* Reference Table */}
      <ReferenceTable marlaInfo={marlaInfo} sheet1={sheet1} />

      {/* 6 House Type Groups — 18 tables total */}
      <div className='space-y-7'>
        {HOUSE_TYPES.map((ht) => (
          <HouseTypeGroup key={ht.id} htId={ht.id} marlaInfo={marlaInfo} sheet1={sheet1} sheet2={sheet2} displayStr={displayStr} />
        ))}
      </div>
    </form>
  );
};

export default CostOfConstruction;